import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { WidePageLayout } from '../components/WidePageLayout';
import RichTextEditor from '../components/RichTextEditor';
import useWikiDetail from '../hooks/useWikiDetail';
import CheckSquareIcon from '../components/icons/CheckSquareIcon';
import type { UpdateWikiRequest } from '../types/epic';

// Form validation schema
const updateWikiSchema = z.object({
  name: z.string().min(1, 'Wiki name is required'),
  slug: z.string().optional(),
  summary: z.string().optional(),
  category: z.string().optional(),
  tags: z.string().optional(),
  content: z.any().optional(),
});

type UpdateWikiFormData = z.infer<typeof updateWikiSchema>;

export function EditWiki() {
  const { id: boardId, wikiId } = useParams<{ id: string; wikiId: string }>();
  const navigate = useNavigate();
  const { wiki, updateWiki } = useWikiDetail(wikiId || '0');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<UpdateWikiFormData>({
    resolver: zodResolver(updateWikiSchema),
    defaultValues: {
      name: '',
      slug: '',
      summary: '',
      category: '',
      tags: '',
      content: [],
    },
  });

  const watchedContent = watch('content');

  // Initialize form with wiki data
  useEffect(() => {
    if (wiki) {
      setValue('name', wiki.name);
      setValue('slug', wiki.slug || '');
      setValue('summary', wiki.summary || '');
      setValue('category', wiki.category || '');
      setValue('tags', wiki.tags ? wiki.tags.join(', ') : '');
      if (wiki.content && typeof wiki.content === 'object') {
        setValue('content', wiki.content as unknown as any[]);
      }
    }
  }, [wiki, setValue]);

  const onSubmit = async (data: UpdateWikiFormData) => {
    const wikiData: UpdateWikiRequest = {
      name: data.name.trim(),
      slug: data.slug?.trim() || undefined,
      summary: data.summary?.trim() || undefined,
      category: data.category?.trim() || undefined,
      tags: data.tags?.trim() ? data.tags.split(',').map((t) => t.trim()) : undefined,
      content: data.content,
    };

    try {
      await updateWiki(wikiData);
      navigate(`/boards/${boardId}/wikis/${wikiId}`);
    } catch {
      // Error is handled by hook
    }
  };

  if (!wiki) {
    return (
      <div className="space-y-6">
        <WidePageLayout>
          <div className="text-center py-12">
            <div className="text-gray-400">Loading wiki...</div>
          </div>
        </WidePageLayout>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <WidePageLayout>
        <div>
          <Link
            to={`/boards/${boardId}/wikis/${wikiId}`}
            className="text-gray-400 hover:text-white transition-colors text-sm"
          >
            ← Back to Wiki
          </Link>
          <h1 className="text-3xl font-bold text-white mt-2">Edit Wiki</h1>
          <p className="text-gray-400 mt-1">Update wiki information</p>
        </div>
      </WidePageLayout>

      <WidePageLayout>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-gray-800 rounded-lg border border-gray-700 p-6"
        >
          {/* Name */}
          <div className="mb-6">
            <label htmlFor="name" className="block text-white font-medium mb-2">
              Wiki Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              {...register('name')}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              placeholder="Enter wiki name..."
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
          </div>

          {/* Slug */}
          <div className="mb-6">
            <label htmlFor="slug" className="block text-white font-medium mb-2">
              URL Slug
            </label>
            <input
              id="slug"
              type="text"
              {...register('slug')}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              placeholder="url-friendly-slug"
            />
            <p className="text-gray-500 text-xs mt-2">
              Leave empty to auto-generate from name. Used in URLs.
            </p>
          </div>

          {/* Summary */}
          <div className="mb-6">
            <label htmlFor="summary" className="block text-white font-medium mb-2">
              Summary
            </label>
            <textarea
              id="summary"
              {...register('summary')}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 resize-none"
              rows={3}
              placeholder="Brief summary of this wiki..."
            />
          </div>

          {/* Category */}
          <div className="mb-6">
            <label htmlFor="category" className="block text-white font-medium mb-2">
              Category
            </label>
            <input
              id="category"
              type="text"
              {...register('category')}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              placeholder="e.g., Documentation, Guides, Tutorials"
            />
          </div>

          {/* Tags */}
          <div className="mb-6">
            <label htmlFor="tags" className="block text-white font-medium mb-2">
              Tags
            </label>
            <input
              id="tags"
              type="text"
              {...register('tags')}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              placeholder="tag1, tag2, tag3"
            />
            <p className="text-gray-500 text-xs mt-2">Separate multiple tags with commas.</p>
          </div>

          {/* Rich Text Content */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">Wiki Content</label>
            {watchedContent && watchedContent.length > 0 ? (
              <RichTextEditor
                value={watchedContent || []}
                onChange={(value) => setValue('content', value)}
                placeholder="Add detailed information to your wiki..."
                className="border-0"
              />
            ) : null}

            <p className="text-gray-500 text-xs mt-2">
              Use the rich text editor to add formatted content, lists, and more.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-700">
            <Link
              to={`/boards/${boardId}/wikis/${wikiId}`}
              className="px-6 py-2 rounded-lg border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <span className="w-5 h-5">
                <CheckSquareIcon />
              </span>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </WidePageLayout>
    </div>
  );
}

export default EditWiki;
